import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  ListSubheader,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  Save as SaveIcon,
  DeleteOutline as DeleteOutlineIcon,
  FilterAltOff as FilterAltOffIcon,
} from '@mui/icons-material';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  AreaChart, 
  Area,
  BarChart, 
  Bar, 
  ComposedChart,
  XAxis, 
  YAxis, 
  Tooltip, 
  Legend,
  Cell 
} from 'recharts';
import { apiRequest } from '../utils/api';
import { positionUnrealizedPL } from '../utils/positionValuation';
import { loadFilterPresets, persistFilterPresets, readLocalPresets } from '../utils/filterPresets';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';
import { usePaperMode } from '../contexts/PaperModeContext';
import {
  fetchAllTrades,
  reconcileTradesForDisplay,
  getUnderlyingSymbol,
  getRealizedValue,
  aggregateClosedPositions,
  parseOptionSymbol,
  groupTradesByStrategy,
  contractTagKey,
  filterClosedPositionRows,
  parseSymbolFilter,
  getClosedTradeNetPL,
  isBankCashFlowRow
} from '../utils/tradeUtils';

const STRATEGY_OPTIONS = [
  { value: 'all', label: 'All Strategies' },
  { value: 'Iron Condor', label: 'Iron Condor' },
  { value: 'Put Credit Spread', label: 'Put Credit Spread' },
  { value: 'Put Debit Spread', label: 'Put Debit Spread' },
  { value: 'Call Credit Spread', label: 'Call Credit Spread' },
  { value: 'Call Debit Spread', label: 'Call Debit Spread' },
  { value: 'Long Straddle', label: 'Long Straddle' },
  { value: 'Short Straddle', label: 'Short Straddle' },
  { value: 'Long Strangle', label: 'Long Strangle' },
  { value: 'Short Strangle', label: 'Short Strangle' },
  { value: 'Short Call', label: 'Short Call' },
  { value: 'Long Call', label: 'Long Call' },
  { value: 'Short Put', label: 'Short Put' },
  { value: 'Long Put', label: 'Long Put' },
  { value: 'Equity', label: 'Equity' },
  { value: 'Dividend', label: 'Dividend' }
];

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { paperMode } = usePaperMode();
  // Persisted settings are stored per trading mode — live uses the bare key,
  // paper prefixes it — so each mode remembers its own filters.
  const modeKey = (name) => `${paperMode ? 'paper_' : ''}${name}`;
  const modeGet = (name, fallbackName) =>
    localStorage.getItem(modeKey(name)) ?? localStorage.getItem(name) ?? (fallbackName ? localStorage.getItem(fallbackName) : null);
  const [loading, setLoading] = useState(true);
  const [plData, setPlData] = useState([]);
  const [symbolData, setSymbolData] = useState([]);
  const [timePeriod, setTimePeriod] = useState(() => modeGet('global_time_period') || '30d');
  const [groupBy, setGroupBy] = useState(() => modeGet('dashboard_group_by') || 'day');
  const [customStartDate, setCustomStartDate] = useState(() => modeGet('global_custom_start') || '');

  // Cache helper functions
  const getCacheKey = () => {
    return `dashboard_${paperMode ? 'paper' : 'live'}_${timePeriod}_${selectedAccount.join('_')}_${groupBy}_${customStartDate}_${customEndDate}`;
  };

  const getCachedData = () => {
    try {
      const key = getCacheKey();
      const cached = localStorage.getItem(key);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const cacheAge = Date.now() - timestamp;
        // Use cache if less than 5 minutes old
        if (cacheAge < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch (e) {
      console.error('Error reading cache:', e);
    }
    return null;
  };

  const setCachedData = (data) => {
    try {
      const key = getCacheKey();
      localStorage.setItem(key, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Error writing cache:', e);
    }
  };
  const [customEndDate, setCustomEndDate] = useState(() => modeGet('global_custom_end') || '');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(() => {
    try {
      const saved = modeGet('global_selected_account');
      return saved ? JSON.parse(saved) : ['all'];
    } catch {
      return ['all'];
    }
  });
  // Draft state for deferred filtering
  const [draftAccount, setDraftAccount] = useState(() => {
    try {
      const saved = modeGet('global_selected_account');
      return saved ? JSON.parse(saved) : ['all'];
    } catch {
      return ['all'];
    }
  });
  // Additional filters (shared with StockSummary via global_* keys)
  const [positionFilter, setPositionFilter] = useState(() => {
    try {
      const saved = modeGet('global_position_filter', 'dashboard_position_filter');
      return saved ? JSON.parse(saved) : ['all'];
    } catch { return ['all']; }
  });
  const [strategyFilter, setStrategyFilter] = useState(() => modeGet('global_strategy_filter', 'dashboard_strategy_filter') || 'all');
  const [symbolFilter, setSymbolFilter] = useState(() => modeGet('global_symbol_filter') || '');
  const [openDateStart, setOpenDateStart] = useState(() => modeGet('global_open_date_start', 'dashboard_open_date_start') || '');
  const [openDateEnd, setOpenDateEnd] = useState(() => modeGet('global_open_date_end', 'dashboard_open_date_end') || '');
  const [tagFilter, setTagFilter] = useState(() => {
    try { return JSON.parse(modeGet('global_tag_filter', 'stocksummary_tag_filter') || '[]'); } catch { return []; }
  });
  const [tagFilterExclude, setTagFilterExclude] = useState(() => {
    try { return JSON.parse(modeGet('global_tag_filter_exclude', 'stocksummary_tag_filter_exclude') || '[]'); } catch { return []; }
  });
  const [availableTags, setAvailableTags] = useState([]);
  const [showFilters, setShowFilters] = useState(() => modeGet('dashboard_show_filters') === 'true');
  const [summaryStats, setSummaryStats] = useState({
    realizedPL: 0,
    unrealizedPL: 0,
    totalPL: 0,
    tradeCount: 0,
    winRate: 0,
    profitFactor: 0,
    avgWeekly: 0,
    avgMonthly: 0
  });
  // Estimated realized P/L from pending (recently-closed, not-yet-settled) positions
  const [pendingPL, setPendingPL] = useState(0);
  // Top winners and losers
  const [topWinners, setTopWinners] = useState([]);
  const [topLosers, setTopLosers] = useState([]);
  // Weekly earnings calendar
  const [weeklyEarnings, setWeeklyEarnings] = useState([]);
  const [earningsLoading, setEarningsLoading] = useState(false);
  // Account balance history
  const [balanceHistory, setBalanceHistory] = useState([]);
  const [balanceLoading, setBalanceLoading] = useState(false);
  // P/L by strategy
  const [strategyPL, setStrategyPL] = useState([]);

  // Group earnings by date
  // Change across the currently displayed balance-history range
  const balanceChange = useMemo(() => {
    if (!balanceHistory || balanceHistory.length < 2) return null;
    const first = Number(balanceHistory[0]?.equity);
    const last = Number(balanceHistory[balanceHistory.length - 1]?.equity);
    if (!Number.isFinite(first) || first === 0 || !Number.isFinite(last)) return null;
    const change = last - first;
    return { change, pct: (change / Math.abs(first)) * 100 };
  }, [balanceHistory]);


  const earningsByDate = React.useMemo(() => {
    const grouped = {};
    weeklyEarnings.forEach(earning => {
      const date = earning.earnings_date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(earning);
    });
    return grouped;
  }, [weeklyEarnings]);

  const sortedDates = Object.keys(earningsByDate).sort();

  // Position pages only deal with investment/brokerage accounts — hide Plaid
  // banking/credit/loan rows. Paper mode shows a single pseudo account since
  // paper positions aren't tied to real accounts.
  const categoryOf = (a) => {
    const cat = String(a.category || '').toLowerCase();
    if (['investment', 'banking', 'credit', 'loan', 'other'].includes(cat)) return cat;
    if (String(a.apiType || a.api_type || '').toLowerCase() === 'plaid') {
      return /credit\s*card|visa|mastercard|amex|discover/i.test(a.name || '') ? 'credit' : 'banking';
    }
    return 'investment';
  };
  const investmentAccounts = accounts.filter(a => categoryOf(a) === 'investment');
  const displayAccounts = paperMode
    ? [{ id: 'paper', name: 'Paper Trading', accountType: 'paper' }]
    : investmentAccounts;

  // Grouped Accounts for dropdown
  const taxableAccounts = displayAccounts.filter(a => a.accountType === 'taxable');
  const nonTaxableAccounts = displayAccounts.filter(a => a.accountType === 'non-taxable');

  const handleAccountCategorySelect = (category) => {
    const categoryAccounts = category === 'taxable' ? taxableAccounts : nonTaxableAccounts;
    const categoryIds = categoryAccounts.map(a => a.id);
    
    // Check if all accounts in this category are already selected
    const allSelected = categoryIds.every(id => draftAccount.includes(id));
    
    let newDraft;
    if (allSelected) {
      // Unselect all in category
      newDraft = draftAccount.filter(id => !categoryIds.includes(id));
    } else {
      // Select all in category
      newDraft = Array.from(new Set([...draftAccount, ...categoryIds])).filter(id => id !== 'all');
    }
    
    if (newDraft.length === 0) newDraft = ['all'];
    setDraftAccount(newDraft);
  };


  // Fetch accounts data
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await apiRequest('/api/accounts?include_inactive=true');
        setAccounts(response.data.value || response.data);
      } catch (error) {
        console.error('Error fetching accounts:', error);
      }
    };
    
    fetchAccounts();
  }, []);

  // Restore the incoming mode's saved settings on live <-> paper toggle.
  // Must run before the persist effects below so it reads the new mode's keys
  // before they are overwritten by same-commit persist writes.
  const prevPaperModeRef = useRef(paperMode);
  useEffect(() => {
    if (prevPaperModeRef.current === paperMode) return;
    prevPaperModeRef.current = paperMode;
    try {
      const json = (k, fb) => { const v = modeGet(k); return v != null ? JSON.parse(v) : fb; };
      setTimePeriod(modeGet('global_time_period') || '30d');
      setGroupBy(modeGet('dashboard_group_by') || 'day');
      setCustomStartDate(modeGet('global_custom_start') || '');
      setCustomEndDate(modeGet('global_custom_end') || '');
      const acct = json('global_selected_account', ['all']);
      setSelectedAccount(acct);
      setDraftAccount(acct);
      setPositionFilter(json('global_position_filter', ['all']));
      setStrategyFilter(modeGet('global_strategy_filter') || 'all');
      setSymbolFilter(modeGet('global_symbol_filter') || '');
      setOpenDateStart(modeGet('global_open_date_start') || '');
      setOpenDateEnd(modeGet('global_open_date_end') || '');
      setShowFilters(modeGet('dashboard_show_filters') !== 'false');
      setTagFilter(json('global_tag_filter', []));
      setTagFilterExclude(json('global_tag_filter_exclude', []));
    } catch (_) {}
  }, [paperMode]);

  // Keep the account selection valid for the current mode — paper mode
  // collapses to 'all' (the Paper Trading pseudo account), live mode drops
  // non-investment (bank/credit) account ids.
  useEffect(() => {
    if (!paperMode && accounts.length === 0) return; // don't wipe saved selection before accounts load
    const valid = new Set(['all', ...displayAccounts.map(a => String(a.id))]);
    const sanitize = (list) => {
      const next = (Array.isArray(list) ? list : ['all']).filter(id => valid.has(String(id)));
      return next.length ? next : ['all'];
    };
    setSelectedAccount(prev => { const n = sanitize(prev); return n.length === prev.length && n.every((v, i) => String(prev[i]) === String(v)) ? prev : n; });
    setDraftAccount(prev => { const n = sanitize(prev); return n.length === prev.length && n.every((v, i) => String(prev[i]) === String(v)) ? prev : n; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperMode, accounts]);

  // Persist time period
  useEffect(() => {
    try { localStorage.setItem(modeKey('global_time_period'), timePeriod); } catch {}
  }, [timePeriod, paperMode]);

  // Persist custom dates
  useEffect(() => {
    try {
      localStorage.setItem(modeKey('global_custom_start'), customStartDate || '');
      localStorage.setItem(modeKey('global_custom_end'), customEndDate || '');
    } catch {}
  }, [customStartDate, customEndDate, paperMode]);

  // Persist selected account
  useEffect(() => {
    try { localStorage.setItem(modeKey('global_selected_account'), JSON.stringify(selectedAccount)); } catch {}
  }, [selectedAccount, paperMode]);

  // Persist additional filters
  useEffect(() => {
    try {
      localStorage.setItem(modeKey('global_position_filter'), JSON.stringify(positionFilter));
      localStorage.setItem(modeKey('global_strategy_filter'), strategyFilter || 'all');
      localStorage.setItem(modeKey('global_symbol_filter'), symbolFilter || '');
      localStorage.setItem(modeKey('global_open_date_start'), openDateStart || '');
      localStorage.setItem(modeKey('global_open_date_end'), openDateEnd || '');
      localStorage.setItem(modeKey('dashboard_show_filters'), showFilters ? 'true' : 'false');
      localStorage.setItem(modeKey('global_tag_filter'), JSON.stringify(tagFilter));
      localStorage.setItem(modeKey('global_tag_filter_exclude'), JSON.stringify(tagFilterExclude));
      localStorage.setItem(modeKey('dashboard_group_by'), groupBy || 'day');
    } catch {}
  }, [positionFilter, strategyFilter, symbolFilter, openDateStart, openDateEnd, showFilters, tagFilter, tagFilterExclude, groupBy, paperMode]);

  // Saved filter presets — persisted server-side (user_preferences) so they
  // follow the user across devices; localStorage is the offline cache.
  const [savedFilters, setSavedFilters] = useState(readLocalPresets);
  const [selectedPreset, setSelectedPreset] = useState(() => modeGet('dashboard_selected_preset') || '');
  const [presetDialog, setPresetDialog] = useState({ open: false, name: '' });

  useEffect(() => {
    let cancelled = false;
    loadFilterPresets().then(p => { if (!cancelled) setSavedFilters(p); });
    return () => { cancelled = true; };
  }, []);

  const DEFAULT_FILTERS = {
    accounts: ['all'],
    timePeriod: '30d',
    customStartDate: '',
    customEndDate: '',
    groupBy: 'day',
    positionFilter: ['all'],
    strategyFilter: 'all',
    symbolFilter: '',
    openDateStart: '',
    openDateEnd: '',
    tagFilter: [],
    tagFilterExclude: [],
  };

  const getCurrentFilterPreset = () => ({
    accounts: selectedAccount,
    timePeriod,
    customStartDate,
    customEndDate,
    groupBy,
    positionFilter,
    strategyFilter,
    symbolFilter,
    openDateStart,
    openDateEnd,
    tagFilter,
    tagFilterExclude,
  });

  const applyFilterPreset = (preset) => {
    const p = { ...DEFAULT_FILTERS, ...preset };
    setSelectedAccount(p.accounts);
    setDraftAccount(p.accounts);
    setTimePeriod(p.timePeriod);
    setCustomStartDate(p.customStartDate);
    setCustomEndDate(p.customEndDate);
    setGroupBy(p.groupBy);
    setPositionFilter(p.positionFilter);
    setStrategyFilter(p.strategyFilter);
    setSymbolFilter(p.symbolFilter);
    setOpenDateStart(p.openDateStart);
    setOpenDateEnd(p.openDateEnd);
    setTagFilter(p.tagFilter || []);
    setTagFilterExclude(p.tagFilterExclude || []);
  };

  const handleSelectPreset = (name) => {
    setSelectedPreset(name);
    localStorage.setItem(modeKey('dashboard_selected_preset'), name || '');
    if (name && savedFilters[name]) applyFilterPreset(savedFilters[name]);
  };

  const handleSavePreset = () => {
    const name = presetDialog.name.trim();
    if (!name) return;
    const next = { ...savedFilters, [name]: getCurrentFilterPreset() };
    setSavedFilters(next);
    persistFilterPresets(next);
    setSelectedPreset(name);
    localStorage.setItem(modeKey('dashboard_selected_preset'), name);
    setPresetDialog({ open: false, name: '' });
  };

  const handleDeletePreset = () => {
    if (!selectedPreset || !savedFilters[selectedPreset]) return;
    const next = { ...savedFilters };
    delete next[selectedPreset];
    setSavedFilters(next);
    persistFilterPresets(next);
    setSelectedPreset('');
    localStorage.setItem(modeKey('dashboard_selected_preset'), '');
  };

  const resetFilters = () => {
    applyFilterPreset(DEFAULT_FILTERS);
    setSelectedPreset('');
    localStorage.setItem(modeKey('dashboard_selected_preset'), '');
  };

  // Fetch account balance history
  useEffect(() => {
    const fetchBalanceHistory = async () => {
      setBalanceLoading(true);
      try {
        const range = getRangeForPeriod(timePeriod);

        if (paperMode) {
          const params = [];
          if (range) params.push(`startDate=${range.startDate}&endDate=${range.endDate}`);
          const url = `/api/paper-trading/balance-history${params.length ? '?' + params.join('&') : ''}`;
          const resp = await apiRequest(url);
          setBalanceHistory(resp.data?.balances || []);
        } else {
          let url = '/api/accounts/balance-history';
          const params = [];
          if (selectedAccount.length > 0 && !selectedAccount.includes('all')) {
            params.push(`accountIds=${selectedAccount.join(',')}`);
          } else if (investmentAccounts.length > 0) {
            // 'All' = all investment accounts — exclude bank/credit balances
            params.push(`accountIds=${investmentAccounts.map(a => a.id).join(',')}`);
          }
          if (range) {
            params.push(`startDate=${range.startDate}&endDate=${range.endDate}`);
          }
          if (params.length > 0) url += `?${params.join('&')}`;
          const resp = await apiRequest(url);
          setBalanceHistory(resp.data?.balances || []);
        }
      } catch (err) {
        console.error('Error fetching balance history:', err);
        setBalanceHistory([]);
      } finally {
        setBalanceLoading(false);
      }
    };
    fetchBalanceHistory();
  }, [selectedAccount, timePeriod, customStartDate, customEndDate, paperMode, accounts]);

  // Fetch weekly earnings
  useEffect(() => {
    const fetchEarnings = async () => {
      setEarningsLoading(true);
      try {
        const response = await apiRequest('/api/earnings/upcoming?days=5');
        console.log('Earnings API response:', response);
        console.log('Earnings data:', response.data);
        console.log('Earnings array:', response.data?.earnings);
        setWeeklyEarnings(response.data?.earnings || []);
        console.log('Set weeklyEarnings to:', response.data?.earnings || []);
      } catch (err) {
        console.error('Error fetching weekly earnings:', err);
        console.error('Error details:', err.response);
      } finally {
        setEarningsLoading(false);
      }
    };
    fetchEarnings();
  }, []);

  const getRangeForPeriod = (p) => {
    const today = new Date();
    const endIso = today.toISOString().split('T')[0];
    const lower = (days) => {
      const d = new Date(today);
      d.setDate(today.getDate() - days);
      return d.toISOString().split('T')[0];
    };
    switch (p) {
      case 'current_month': {
        const y = today.getFullYear();
        const m = today.getMonth();
        const startIso = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        return { startDate: startIso, endDate: endIso };
      }
      case 'previous_month': {
        const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const y = d.getFullYear();
        const m = d.getMonth();
        const startIso = `${y}-${String(m + 1).padStart(2, '0')}-01`;
        const lastDay = new Date(y, m + 1, 0).getDate();
        const endIso2 = `${y}-${String(m + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
        return { startDate: startIso, endDate: endIso2 };
      }
      case '30d': return { startDate: lower(30), endDate: endIso };
      case '60d': return { startDate: lower(60), endDate: endIso };
      case '90d': return { startDate: lower(90), endDate: endIso };
      case '180d': return { startDate: lower(180), endDate: endIso };
      case 'ytd': {
        const y = today.getFullYear();
        const startIso = `${y}-01-01`;
        return { startDate: startIso, endDate: endIso };
      }
      case 'last_year': {
        const y = today.getFullYear() - 1;
        const startIso = `${y}-01-01`;
        const end = `${y}-12-31`;
        return { startDate: startIso, endDate: end };
      }
      case 'custom':
        return (customStartDate && customEndDate)
          ? { startDate: customStartDate, endDate: customEndDate }
          : null;
      default:
        return null;
    }
  };

  // Fetch P/L data
  useEffect(() => {
    const fetchData = async () => {
      // Try to load from cache first for instant display
      const cached = getCachedData();
      if (cached) {
        setPlData(cached.plData || []);
        setSummaryStats(cached.summaryStats || summaryStats);
        setLoading(false);
      }

      // Always fetch fresh data in background
      try {
        // Fetch all trades, filter by account client-side (matching StockSummary)
        const tradesBaseUrl = paperMode ? '/api/paper-trading/trades-formatted' : '/api/trades';
        let tradesUrl = tradesBaseUrl;
        const tradesParams = [];

        if (timePeriod === 'custom' && customStartDate && customEndDate) {
          tradesParams.push(`startDate=${customStartDate}&endDate=${customEndDate}`);
        } else {
          const range = getRangeForPeriod(timePeriod);
          if (range) {
            tradesParams.push(`startDate=${range.startDate}&endDate=${range.endDate}`);
          }
        }
        
        // Include open/unclosed trades (closing_date is null) so client-side FIFO
        // has the opening legs — matches StockSummary's fetch and keeps reconcile
        // semantics identical across both pages.
        tradesParams.push('includeOpen=true');

        if (tradesParams.length > 0) {
          tradesUrl += `?${tradesParams.join('&')}`;
        }

        // Fetch all trades and reconcile
        const trades = await fetchAllTrades(tradesUrl);
        const reconciledTrades = reconcileTradesForDisplay(trades);

        // Open-trade info keyed by account|contract — broker positions carry no
        // tags/strategy/open-date, so they're resolved from the open trades
        // behind them. Mirrors StockSummary's openTradeTagsByKey so both pages
        // filter identically.
        const openTradeInfoByKey = new Map();
        const tagSet = new Set();
        const infoFor = (key) => {
          if (!key) return null;
          if (!openTradeInfoByKey.has(key)) {
            openTradeInfoByKey.set(key, { tags: new Set(), strats: new Set(), openDate: null });
          }
          return openTradeInfoByKey.get(key);
        };
        reconciledTrades.forEach(t => {
          (Array.isArray(t.tags) ? t.tags : []).forEach(t => t && tagSet.add(t));
          if (t.closingDate || t.closing_date) return;
          if (t._hideOpeningLeg || t.isMatched === true || t.is_matched === true) return;
          const acc = t.accountId || t.account_id;
          if (!acc) return;
          const dateStr = String(t.tradeDate || t.trade_date || t.openDate || '').slice(0, 10);
          [contractTagKey(t.originalSymbol || t.original_symbol), contractTagKey(t.symbol)].forEach(key => {
            const info = infoFor(key && `${acc}|${key}`);
            if (!info) return;
            (Array.isArray(t.tags) ? t.tags : []).forEach(x => { if (x) { info.tags.add(x); tagSet.add(x); } });
            if (t.strategy) info.strats.add(t.strategy);
            if (dateStr && (!info.openDate || dateStr < info.openDate)) info.openDate = dateStr;
          });
        });
        setAvailableTags([...tagSet].sort((a, b) => a.localeCompare(b)));

        const openInfoFor = (symbol, accountId) =>
          accountId != null ? openTradeInfoByKey.get(`${accountId}|${contractTagKey(symbol)}`) : null;

        // Tags for an open position: its own tags + the open-trade lookup.
        const positionTagsFor = (symbol, accountId, ownTags) => {
          const out = new Set(Array.isArray(ownTags) ? ownTags : []);
          const s = openInfoFor(symbol, accountId)?.tags;
          if (s) s.forEach(t => out.add(t));
          return [...out];
        };
        const tagsMatch = (tags) => {
          const hasInc = tagFilter && tagFilter.length > 0;
          const hasExc = tagFilterExclude && tagFilterExclude.length > 0;
          if (!hasInc && !hasExc) return true;
          if (hasExc && tagFilterExclude.some(t => tags.includes(t))) return false;
          if (hasInc && !tagFilter.some(t => tags.includes(t))) return false;
          return true;
        };

        // Apply account filter first (used for both symbol data and trade stats)
        // includeOpen pulls opening legs so FIFO has lots to consume; drop still-open
        // rows afterwards so stats reflect the same closed row set as before.
        // Reconcile can synthesize closing rows (expirations/assignments) dated
        // before the selected range, so re-apply the period bounds on closingDate
        // to match what the server's closing_date filter would have returned.
        const effectiveRange = (timePeriod === 'custom' && customStartDate && customEndDate)
          ? { startDate: customStartDate, endDate: customEndDate }
          : getRangeForPeriod(timePeriod);
        // Row inclusion mirrors StockSummary's pivotInput: bound on
        // closingDate || tradeDate, then keep closing legs / dividend rows
        // while dropping still-open opening legs. Bank cash-flow rows
        // (Plaid depository income) are not positions and are dropped here.
        let filteredTrades = reconciledTrades.filter(trade => {
          if (isBankCashFlowRow(trade)) return false;
          const td = trade.closingDate || trade.tradeDate || trade.trade_date;
          if (!td) return false;
          const d = String(td).slice(0, 10);
          if (effectiveRange) {
            if (effectiveRange.startDate && d < effectiveRange.startDate) return false;
            if (effectiveRange.endDate && d > effectiveRange.endDate) return false;
          }
          return true;
        });
        filteredTrades = filteredTrades.filter(trade => {
          if (trade.closingDate) return true;
          if (trade._hideOpeningLeg) return false;
          if (trade._closedSide || trade._isClosing) return true;
          const tt = String(trade.tradeType || trade.trade_type || trade.transactionType || trade.transaction_type || '').toUpperCase();
          const ttNorm = tt.replace(/_/g, ' ');
          if (tt === 'DIVIDEND' || tt === 'DIV') return true;
          if (tt === 'SELL' || tt === 'STC' || tt === 'BTC' || ttNorm.includes('TO CLOSE') || tt === 'OEXP' || tt === 'OASGN' || tt === 'ASSIGNMENT' || tt === 'EXPIRE') return true;
          return false;
        });
        if (!selectedAccount.includes('all')) {
          // Filter the already date-bounded set — refiltering reconciledTrades
          // here would drop the period bounds and leak out-of-range rows into
          // the chart and stats.
          filteredTrades = filteredTrades.filter(trade => {
            const accId = String(trade.accountId || trade.account_id || '');
            const selectedAccountIds = selectedAccount.map(id => String(id));
            return selectedAccountIds.includes(accId);
          });
        }

        // Group into underlying rows (dedup + AGG skip, same as StockSummary's
        // symbolTradesMap), apply the shared closed-position filter, then
        // flatten back so every downstream stat sees the identical trade set
        // Position Summary uses.
        const symbolRowsMap = new Map();
        const seenTradeIds = new Set();
        filteredTrades.forEach(trade => {
          if (trade.tradeType === 'AGG') return;
          const symbol = getUnderlyingSymbol(trade);
          if (!symbol) return;
          const tradeId = trade.id || trade.external_id || `${trade.symbol}_${trade.tradeDate}_${trade.quantity}_${trade.price}`;
          if (seenTradeIds.has(tradeId)) return;
          seenTradeIds.add(tradeId);
          if (!symbolRowsMap.has(symbol)) symbolRowsMap.set(symbol, { symbol, trades: [] });
          symbolRowsMap.get(symbol).trades.push(trade);
        });
        const filteredRows = filterClosedPositionRows(Array.from(symbolRowsMap.values()), {
          symbolFilter,
          strategyFilter,
          openDateStart,
          openDateEnd,
          positionFilter,
          tagFilter,
          tagFilterExclude,
          tagLookup: (entity) => {
            const out = new Set();
            const look = (e) => {
              if (!e) return;
              const acc = e.accountId || e.account_id;
              [e.originalSymbol, e.original_symbol, e.symbol].forEach(sym => {
                const info = acc != null ? openTradeInfoByKey.get(`${acc}|${contractTagKey(sym)}`) : null;
                if (info) info.tags.forEach(x => out.add(x));
              });
              (Array.isArray(e.trades) ? e.trades : []).forEach(look);
              (Array.isArray(e.legs) ? e.legs : []).forEach(look);
              if (e.position) look(e.position);
            };
            look(entity);
            return [...out];
          },
        });
        filteredTrades = filteredRows.flatMap(r => r.trades || []);

        // Filter valid rows for symbol aggregation (closed positions / realized P/L / dividends)
        const validRows = filteredTrades.filter(r =>
          !r._hideOpeningLeg && 
          !r._hideDueToAggregation &&
          (Math.abs(getClosedTradeNetPL(r)) > 0.001 ||
           Math.abs(parseFloat(r.dividend || r.dividend_amount || 0)) > 0.001 ||
           r.tradeType === 'AGG')
        );
        
        // Group by underlying symbol
        const symbolMap = new Map();
        
        validRows.forEach(row => {
          // Pass the whole row to getUnderlyingSymbol for robust extraction
          const underlying = getUnderlyingSymbol(row);
          const pl = getClosedTradeNetPL(row);
          
          if (!symbolMap.has(underlying)) {
            symbolMap.set(underlying, {
              symbol: underlying,
              pl: 0,
              tradeCount: 0,
              fullSymbols: [],
              name: underlying
            });
          }
          const entry = symbolMap.get(underlying);
          entry.pl += pl;
          entry.tradeCount += 1;
          entry.fullSymbols.push(row.symbol);
        });
        
        // Convert to array and separate winners and losers
        const allSymbols = Array.from(symbolMap.values());
        const winners = allSymbols
          .filter(item => item.pl > 0)
          .sort((a, b) => b.pl - a.pl)
          .slice(0, 10); // Top 10 winners
        const losers = allSymbols
          .filter(item => item.pl < 0)
          .sort((a, b) => a.pl - b.pl)
          .slice(0, 10); // Top 10 losers
          
        const formattedSymbolData = [...winners, ...losers]
          .map(item => ({
            ...item,
            pl: parseFloat(item.pl.toFixed(2)) // Keep as number for Recharts
          }));
        
        setSymbolData(formattedSymbolData);
        
        // Fetch open positions for unrealized P/L
        let unrealizedPL = 0;
        try {
          // Helper to check if a position passes the current filters — same
          // fields StockSummary applies to open positions: symbol, position
          // type, open date, strategy, and tags.
          const passesFilters = (symbol, isOption, opts = {}) => {
            const { accountId, tags: ownTags, openDate: posOpenDate, quantity } = opts;
            // Symbol filter
            if (symbolFilter && symbolFilter.trim()) {
              const tokens = symbolFilter.trim().split(/\s+/).filter(Boolean);
              const incTerms = tokens.filter(t => !t.startsWith('-')).map(t => t.toUpperCase());
              const excTerms = tokens.filter(t => t.startsWith('-')).map(t => t.slice(1).toUpperCase());
              const checkStr = symbol.toUpperCase();
              if (incTerms.length > 0 && !incTerms.some(t => checkStr.includes(t))) return false;
              if (excTerms.length > 0 && excTerms.some(t => checkStr.includes(t))) return false;
            }
            // Position type filter
            if (positionFilter && !positionFilter.includes('all')) {
              const isStock = !isOption;
              const matchesOption = positionFilter.includes('options') && isOption;
              const matchesStock = positionFilter.includes('stocks') && isStock;
              if (!matchesOption && !matchesStock) return false;
            }
            // Open date range — falls back to the opening trade's date
            if (openDateStart || openDateEnd) {
              const od = String(posOpenDate || openInfoFor(symbol, accountId)?.openDate || '').slice(0, 10);
              if (od) {
                if (openDateStart && od < openDateStart) return false;
                if (openDateEnd && od > openDateEnd) return false;
              }
            }
            // Strategy filter — single legs derive direction from quantity;
            // spreads match via the strategy recorded on the opening trades
            if (strategyFilter && strategyFilter !== 'all') {
              if (strategyFilter === 'Equity') {
                if (isOption) return false;
              } else if (strategyFilter === 'Dividend') {
                return false;
              } else {
                let stratMatch = false;
                if (isOption && ['Short Call', 'Long Call', 'Short Put', 'Long Put'].includes(strategyFilter)) {
                  const info = parseOptionSymbol(symbol);
                  const dir = parseFloat(quantity || 0) < 0 ? 'Short' : 'Long';
                  const type = info?.type === 'P' ? 'Put' : 'Call';
                  stratMatch = `${dir} ${type}` === strategyFilter;
                }
                if (!stratMatch) {
                  const strats = openInfoFor(symbol, accountId)?.strats;
                  stratMatch = !!strats && [...strats].some(s => (s || '').includes(strategyFilter));
                }
                if (!stratMatch) return false;
              }
            }
            // Tag filter — tags resolve from the open trades behind the position
            if (!tagsMatch(positionTagsFor(symbol, accountId, ownTags))) return false;
            return true;
          };

          if (paperMode) {
            // Paper mode: fetch paper positions and compute unrealized P/L with live quotes
            const paperResponse = await apiRequest('/api/paper-trading/positions');
            let paperPositions = paperResponse.data?.data || paperResponse.data || [];

            // Filter by symbol and position type
            paperPositions = paperPositions.filter(pos => {
              const isOption = (pos.asset_type || pos.assetType) !== 'EQUITY';
              return passesFilters(pos.symbol, isOption, {
                accountId: pos.account_id || pos.accountId,
                tags: pos.tags,
                openDate: pos.open_date || pos.openDate || pos.opened_at,
                quantity: pos.quantity,
              });
            });

            if (paperPositions.length > 0) {
              // Group positions by their linked account_id for quote fetching
              const byAccount = {};
              paperPositions.forEach(pos => {
                const acctId = pos.account_id || pos.accountId;
                if (!byAccount[acctId]) byAccount[acctId] = [];
                byAccount[acctId].push(pos);
              });

              for (const [acctId, positions] of Object.entries(byAccount)) {
                try {
                  const symbols = positions.map(p => p.symbol).join(',');
                  let quotesLookup = {};

                  // Try to fetch quotes from the linked real account
                  if (acctId && acctId !== 'null') {
                    try {
                      const quotesResponse = await apiRequest(`/api/accounts/${acctId}/quotes`, {
                        method: 'POST',
                        body: JSON.stringify({ symbols })
                      });
                      const quotes = quotesResponse.data?.quotes?.quotes?.quote || quotesResponse.data?.quotes?.quote || [];
                      const quotesArray = Array.isArray(quotes) ? quotes : [quotes];
                      quotesArray.forEach(q => { quotesLookup[q.symbol] = q; });
                    } catch (qErr) {
                      console.error('Error fetching quotes for paper positions:', qErr);
                    }
                  }

                  const accountUnrealizedPL = positions.reduce((sum, position) => {
                    const isOption = (position.asset_type || position.assetType) !== 'EQUITY';
                    const avgPrice = parseFloat(position.avg_fill_price || 0);
                    const qty = parseInt(position.quantity || 0);
                    const instruction = position.instruction || '';
                    const isShort = instruction.includes('SELL') || instruction.includes('SHORT');
                    const absQty = Math.abs(qty);
                    const costBasis = avgPrice * absQty * (isOption ? 100 : 1);
                    const normalized = {
                      symbol: position.symbol,
                      isOption,
                      quantity: isShort ? -absQty : absQty,
                      cost_basis: isShort ? -costBasis : costBasis,
                    };
                    const quote = quotesLookup[position.symbol]
                      || quotesLookup[String(position.symbol || '').replace(/\s+/g, '')];
                    return sum + positionUnrealizedPL(normalized, quote);
                  }, 0);

                  unrealizedPL += accountUnrealizedPL;
                } catch (error) {
                  console.error(`Error processing paper positions for account ${acctId}:`, error);
                }
              }
            }
          } else {
          const targetAccountIds = selectedAccount.includes('all')
            ? investmentAccounts.map(a => a.id)
            : selectedAccount;

          for (const accountId of targetAccountIds) {
            try {
              const account = accounts.find(a => a.id === accountId);
              // Skip disabled accounts
              if (!account || !account.isActive) continue;

              // Manual accounts hold DB positions — /positions 400s for them.
              // Mirror StockSummary: load DB rows, value via Schwab quotes (or
              // the recorded holding price when no broker quote is reachable).
              if (String(account.apiType || account.api_type || '').toLowerCase() === 'manual') {
                const dbResp = await apiRequest(`/api/trades/positions/open?accountId=${accountId}`);
                const responseData = dbResp.data || {};
                const dbPositions = Array.isArray(responseData.openPositions)
                  ? responseData.openPositions
                  : (Array.isArray(responseData) ? responseData : []);
                const manualPositions = dbPositions.map(pos => {
                  const rawQty = parseFloat(pos.totalQuantity ?? pos.quantity ?? 0);
                  const firstTrade = pos.trades?.[0] || {};
                  const originalSymbol = firstTrade.originalSymbol || pos.original_symbol || pos.originalSymbol || '';
                  const optionInfo = parseOptionSymbol(originalSymbol || pos.symbol || '');
                  const isOption = optionInfo.isOption;
                  const displaySymbol = isOption ? originalSymbol : (pos.symbol || pos.underlying || '');
                  const tt = String(firstTrade.transactionType || firstTrade.tradeType || pos.transaction_type || pos.trade_type || '').toUpperCase();
                  const isShort = tt === 'STO' || tt === 'SELL_TO_OPEN' || tt === 'SELL' || tt === 'SHORT';
                  const avgPrice = parseFloat(pos.avgPrice ?? pos.averagePrice ?? pos.price ?? pos.average_price ?? 0);
                  const totalCost = parseFloat(pos.totalCost ?? pos.cost ?? 0);
                  const costBasis = totalCost || (avgPrice * Math.abs(rawQty) * (isOption ? 100 : 1));
                  const rawData = firstTrade.rawData || pos.raw_data || pos.rawData || null;
                  return {
                    symbol: displaySymbol,
                    quantity: isShort ? -Math.abs(rawQty) : Math.abs(rawQty),
                    cost_basis: isShort ? -costBasis : costBasis,
                    marketValue: parseFloat(pos.marketValue || pos.market_value || 0),
                    isOption,
                    openDate: pos.openDate || pos.earliestTradeDate || null,
                    _holdingPrice: parseFloat(rawData?.price || 0),
                  };
                }).filter(p => Math.abs(p.quantity) > 0 && passesFilters(p.symbol, p.isOption, {
                  accountId,
                  openDate: p.openDate,
                  quantity: p.quantity,
                }));

                if (manualPositions.length > 0) {
                  const schwabAccount = accounts.find(a => a.isActive && (a.apiType || a.api_type || '').toLowerCase() === 'schwab');
                  const manualQuoteMap = {};
                  if (schwabAccount) {
                    try {
                      const quoteResponse = await apiRequest('/api/schwab-api/quotes', {
                        method: 'POST',
                        body: JSON.stringify({
                          accountId: schwabAccount.id,
                          symbols: [...new Set(manualPositions.map(p => p.symbol).filter(Boolean))],
                        }),
                      });
                      const quotes = quoteResponse.data?.quotes || {};
                      Object.entries(quotes).forEach(([k, v]) => {
                        manualQuoteMap[String(k).replace(/\s+/g, '').toUpperCase()] = v;
                      });
                    } catch (qErr) {
                      console.warn('Failed to fetch quotes for manual positions:', qErr.message);
                    }
                  }
                  unrealizedPL += manualPositions.reduce((sum, p) => {
                    const key = String(p.symbol || '').replace(/\s+/g, '').toUpperCase();
                    const quote = manualQuoteMap[key]
                      || (p._holdingPrice > 0 ? { last: p._holdingPrice } : null);
                    return sum + positionUnrealizedPL(p, quote);
                  }, 0);
                }
                continue;
              }

              const positionsUrl = `/api/accounts/${accountId}/positions`;
              const positionsResponse = await apiRequest(positionsUrl);
              // console.log(`Dashboard positions for account ${accountId}:`, positionsResponse.data);

              // Normalize positions from various broker API shapes
              let positions = [];
              const rawPositions = positionsResponse.data?.positions;
              if (Array.isArray(rawPositions)) {
                positions = rawPositions;
              } else if (rawPositions) {
                if (Array.isArray(rawPositions.position)) {
                  positions = rawPositions.position;
                } else if (Array.isArray(rawPositions.positions)) {
                  positions = rawPositions.positions;
                } else if (rawPositions.positions?.position) {
                  positions = Array.isArray(rawPositions.positions.position)
                    ? rawPositions.positions.position
                    : [rawPositions.positions.position];
                } else if (rawPositions.position) {
                  positions = Array.isArray(rawPositions.position)
                    ? rawPositions.position
                    : [rawPositions.position];
                }
              }
              let positionsArray = Array.isArray(positions) ? positions : [positions];
              // Filter by symbol and position type
              positionsArray = positionsArray.filter(pos => {
                const sym = pos.symbol || pos.instrument?.symbol || '';
                const isOption = sym.includes('_') || /[A-Z]+\d{6}[CP]\d+/.test(sym);
                return passesFilters(sym, isOption, {
                  accountId,
                  tags: pos.tags,
                  openDate: pos.openDate || pos.acquiredDate || pos.open_date,
                  quantity: pos.quantity ?? ((pos.longQuantity || 0) - (pos.shortQuantity || 0)),
                });
              });
              // console.log(`Dashboard positions array for account ${accountId}:`, positionsArray);
              
              if (positionsArray.length > 0) {
                const symbols = positionsArray.map(pos => pos.symbol || pos.instrument?.symbol || '').filter(Boolean).join(',');
                const quotesResponse = await apiRequest(`/api/accounts/${accountId}/quotes`, {
                  method: 'POST',
                  body: JSON.stringify({ symbols })
                });
                // console.log(`Dashboard quotes for account ${accountId}:`, quotesResponse.data);
                // Normalize quotes from various broker API shapes
                let quotes = [];
                const rawQuotes = quotesResponse.data?.quotes;
                if (Array.isArray(rawQuotes)) {
                  quotes = rawQuotes;
                } else if (rawQuotes) {
                  if (Array.isArray(rawQuotes.quote)) {
                    quotes = rawQuotes.quote;
                  } else if (Array.isArray(rawQuotes.quotes)) {
                    quotes = rawQuotes.quotes;
                  } else if (rawQuotes.quotes?.quote) {
                    quotes = Array.isArray(rawQuotes.quotes.quote)
                      ? rawQuotes.quotes.quote
                      : [rawQuotes.quotes.quote];
                  } else if (rawQuotes.quote) {
                    quotes = Array.isArray(rawQuotes.quote)
                      ? rawQuotes.quote
                      : [rawQuotes.quote];
                  }
                }
                const quotesArray = Array.isArray(quotes) ? quotes : [quotes];
                const quotesLookup = {};
                quotesArray.forEach(quote => {
                  if (quote?.symbol) {
                    quotesLookup[quote.symbol] = quote;
                    quotesLookup[String(quote.symbol).replace(/\s+/g, '')] = quote;
                  }
                });

                // Canonical valuation shared with StockSummary: quote-derived
                // market value (mid/mark-first), broker marketValue fallback,
                // signed-quantity direction.
                const accountUnrealizedPL = positionsArray.reduce((sum, position) => {
                  const sym = position.symbol || position.instrument?.symbol || '';
                  const quote = quotesLookup[sym] || quotesLookup[String(sym).replace(/\s+/g, '')];
                  return sum + positionUnrealizedPL(position, quote);
                }, 0);
                // console.log(`Dashboard account ${accountId} unrealized P/L:`, accountUnrealizedPL);
                
                unrealizedPL += accountUnrealizedPL;
              }
            } catch (error) {
              console.error(`Error fetching positions for account ${accountId}:`, error);
            }
          }
          }
        } catch (error) {
          console.error('Error fetching unrealized P/L:', error);
        }

        // Use shared aggregation function with same parameters as StockSummary
        const range = getRangeForPeriod(timePeriod);
        const { symbolData: aggregatedData, totalsRow: aggregatedTotals } = aggregateClosedPositions(filteredTrades, {
          groupBy: 'month', // StockSummary uses month grouping by default
          startDate: range?.startDate,
          endDate: range?.endDate,
          accountIds: selectedAccount.includes('all') ? [] : selectedAccount
          // Don't pass positionFilter to include all position types
        });

        console.log('DEBUG Dashboard from aggregation:', {
          totalPL: aggregatedTotals.totalPL,
          totalDividendOnly: aggregatedTotals.totalDividendOnly,
          realizedPL: aggregatedTotals.totalPL - aggregatedTotals.totalDividendOnly,
          numSymbols: aggregatedData.length
        });

        const closedPL = aggregatedTotals.totalPL - aggregatedTotals.totalDividendOnly;
        const closedDividend = aggregatedTotals.totalDividendOnly;
        const dividend = closedDividend;
        const realizedPL = closedPL;
        const totalPL = closedPL + unrealizedPL;
        const totalTrades = filteredTrades.length;
        
        // Calculate win rate from trades
        const winningTrades = filteredTrades.filter(r => {
          const realized = getClosedTradeNetPL(r);
          return realized > 0;
        });
        const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0;
        
        // Calculate profit factor from all trades
        const totalWinnings = filteredTrades
          .filter(r => getClosedTradeNetPL(r) > 0)
          .reduce((sum, r) => sum + getClosedTradeNetPL(r), 0);

        const totalLosses = Math.abs(filteredTrades
          .filter(r => getClosedTradeNetPL(r) < 0)
          .reduce((sum, r) => sum + getClosedTradeNetPL(r), 0));
        
        const profitFactor = totalLosses > 0 ? (totalWinnings / totalLosses) : (totalWinnings > 0 ? 999 : 0);

        // Calculate average weekly and monthly P/L based on time period
        let weeks = 0;
        let months = 0;

        if (range && range.startDate && range.endDate) {
          const start = new Date(range.startDate);
          const end = new Date(range.endDate);
          const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          weeks = Math.max(daysDiff / 7, 1);
          months = Math.max(daysDiff / 30, 1);
        } else {
          // Default values based on time period
          const daysMap = { '30d': 30, '60d': 60, '90d': 90, '180d': 180 };
          const days = daysMap[timePeriod] || 30;
          weeks = Math.max(days / 7, 1);
          months = Math.max(days / 30, 1);
        }

        const avgWeekly = weeks > 0 ? realizedPL / weeks : 0;
        const avgMonthly = months > 0 ? realizedPL / months : 0;

        // Build P/L chart data from filteredTrades (so all filters apply to the chart)
        const plPeriodMap = new Map();
        const chartTradeIdSet = new Set();
        const getPeriodKey = (dateStr) => {
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return null;
          if (groupBy === 'day') {
            return d.toISOString().split('T')[0];
          } else if (groupBy === 'week') {
            const monday = new Date(d);
            const day = d.getDay();
            const diff = d.getDate() - day + (day === 0 ? -6 : 1);
            monday.setDate(diff);
            return monday.toISOString().split('T')[0];
          } else {
            return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
          }
        };

        filteredTrades.forEach(trade => {
          // Skip synthetic aggregate rows; they are not actual user trades and are not in the summary
          if (trade.tradeType === 'AGG') return;

          const td = trade.closingDate || trade.tradeDate || trade.trade_date;
          if (!td) return;
          const key = getPeriodKey(td);
          if (!key) return;

          // Deduplicate to match the summary aggregation
          const tradeId = trade.id || trade.external_id || `${trade.symbol}_${trade.tradeDate}_${trade.quantity}_${trade.price}`;
          if (chartTradeIdSet.has(tradeId)) return;
          chartTradeIdSet.add(tradeId);

          if (!plPeriodMap.has(key)) {
            plPeriodMap.set(key, { period: key, realizedPL: 0, tradeCount: 0 });
          }
          const entry = plPeriodMap.get(key);
          const realized = getClosedTradeNetPL(trade);
          entry.realizedPL += realized;
          entry.tradeCount += 1;
        });

        const sortedPeriods = Array.from(plPeriodMap.values()).sort((a, b) => a.period.localeCompare(b.period));
        let cumulativeRealizedPL = 0;
        const formattedPlData = sortedPeriods.map(item => {
          cumulativeRealizedPL += item.realizedPL;
          return {
            period: item.period,
            pl: item.realizedPL.toFixed(2),
            realizedPL: item.realizedPL.toFixed(2),
            unrealizedPL: '0',
            cumulativePL: cumulativeRealizedPL.toFixed(2),
            cumulativeRealizedPL: cumulativeRealizedPL.toFixed(2),
            cumulativeUnrealizedPL: '0',
            tradeCount: item.tradeCount
          };
        });

        setPlData(formattedPlData);

        // Calculate top 5 winners and losers by symbol — same canonical P/L as
        // the rest of the page: getClosedTradeNetPL (netPL/_realizedSynth aware,
        // net of fees) and getUnderlyingSymbol, skipping AGG synthetic rows.
        const symbolPL = {};
        validRows.forEach(row => {
          if (row.tradeType === 'AGG') return;
          const symbol = getUnderlyingSymbol(row) || row.symbol || 'Unknown';
          const pl = getClosedTradeNetPL(row);
          if (!symbolPL[symbol]) {
            symbolPL[symbol] = 0;
          }
          symbolPL[symbol] += pl;
        });

        const sortedSymbols = Object.entries(symbolPL)
          .map(([symbol, pl]) => ({ symbol, pl }))
          .sort((a, b) => b.pl - a.pl);

        const topWinnersList = sortedSymbols.filter(r => r.pl > 0).slice(0, 5);
        const topLosersList = sortedSymbols.filter(r => r.pl < 0).sort((a, b) => a.pl - b.pl).slice(0, 5);

        setTopWinners(topWinnersList);
        setTopLosers(topLosersList);

        // Compute P/L by strategy using the same groupTradesByStrategy as StockSummary
        // Apply the same date range filtering as aggregateClosedPositions
        // Also skip AGG trades to match StockSummary's pivot which excludes them
        let strategyTrades = filteredTrades.filter(t => t.tradeType !== 'AGG');
        if (range?.startDate) {
          strategyTrades = strategyTrades.filter(t => {
            const td = t.closingDate || t.tradeDate || t.trade_date;
            return td && td >= range.startDate;
          });
        }
        if (range?.endDate) {
          strategyTrades = strategyTrades.filter(t => {
            const td = t.closingDate || t.tradeDate || t.trade_date;
            return td && td <= range.endDate;
          });
        }
        const strategyGroups = groupTradesByStrategy(strategyTrades);
        const strategyMap = {};

        strategyGroups.forEach(group => {
          const strategy = group.strategyType;
          const pl = group.totalPL + group.totalDividend;
          const cost = Math.abs(group.price * group.quantity * 100) || 0;

          if (!strategyMap[strategy]) {
            strategyMap[strategy] = { strategy, pl: 0, count: 0, cost: 0 };
          }
          strategyMap[strategy].pl += pl;
          strategyMap[strategy].count += 1;
          strategyMap[strategy].cost += cost;
        });

        const strategyList = Object.values(strategyMap)
          .map(s => ({
            ...s,
            pl: parseFloat(s.pl.toFixed(2)),
            plPct: s.cost > 0 ? parseFloat((s.pl / s.cost * 100).toFixed(1)) : 0
          })
          )
          .sort((a, b) => b.pl - a.pl);

        setStrategyPL(strategyList);

        // console.log('Dashboard final summary stats calculation:');
        // console.log('realizedPL:', realizedPL);
        // console.log('unrealizedPL:', unrealizedPL);
        // console.log('totalPL:', totalPL);
        // console.log('weeks:', weeks, 'avgWeekly:', avgWeekly);
        // console.log('months:', months, 'avgMonthly:', avgMonthly);

        setSummaryStats({
          realizedPL: realizedPL.toFixed(2),
          dividend: dividend.toFixed(2),
          unrealizedPL: unrealizedPL.toFixed(2),
          totalPL: totalPL.toFixed(2),
          tradeCount: totalTrades,
          winRate: winRate.toFixed(2),
          profitFactor: profitFactor.toFixed(2),
          avgWeekly: avgWeekly.toFixed(2),
          avgMonthly: avgMonthly.toFixed(2)
        });

        // Save to cache for next load
        setCachedData({
          plData: formattedPlData,
          summaryStats: {
            realizedPL: realizedPL.toFixed(2),
            dividend: dividend.toFixed(2),
            unrealizedPL: unrealizedPL.toFixed(2),
            totalPL: totalPL.toFixed(2),
            tradeCount: totalTrades,
            winRate: winRate.toFixed(2),
            profitFactor: profitFactor.toFixed(2),
            avgWeekly: avgWeekly.toFixed(2),
            avgMonthly: avgMonthly.toFixed(2)
          }
        });

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [timePeriod, groupBy, customStartDate, customEndDate, selectedAccount, accounts, paperMode, positionFilter, strategyFilter, symbolFilter, openDateStart, openDateEnd, tagFilter, tagFilterExclude]);

  // Fetch estimated pending (recently-closed, not-yet-settled) realized P/L and
  // fold it into the Closed Trade P/L card. Only counted when the selected
  // period includes today, since pending fills are from the last few days.
  useEffect(() => {
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const includesToday = (() => {
          if (timePeriod === 'last_year') return false;
          if (timePeriod === 'custom') {
            if (!customEndDate) return true;
            const today = new Date().toISOString().split('T')[0];
            return customEndDate >= today;
          }
          return true;
        })();
        if (!includesToday) {
          if (!cancelled) setPendingPL(0);
          return;
        }

        const acct = selectedAccount.includes('all') ? 'all' : selectedAccount.join(',');
        const resp = await apiRequest(`/api/pending-positions?accountId=${acct}`);
        const list = (resp && resp.data && Array.isArray(resp.data.pending)) ? resp.data.pending : [];
        const range = timePeriod === 'custom'
          ? { startDate: customStartDate, endDate: customEndDate }
          : getRangeForPeriod(timePeriod);
        // Same pending filters as StockSummary: position type + symbol + date.
        const { include: incTerms, exclude: excTerms } = parseSymbolFilter(symbolFilter);
        const hasPositionFilter = positionFilter && !positionFilter.includes('all');
        const filteredPending = list.filter((p) => {
          if (hasPositionFilter) {
            const raw = p.originalSymbol || p.symbol || '';
            const info = parseOptionSymbol(raw);
            const isOption = info && info.isOption;
            const matchesOption = positionFilter.includes('options') && isOption;
            const matchesStock = positionFilter.includes('stocks') && !isOption;
            if (!matchesOption && !matchesStock) return false;
          }
          if (incTerms.length > 0 || excTerms.length > 0) {
            const sym = (p.symbol || p.originalSymbol || '').toLowerCase();
            if (excTerms.some(t => sym.includes(t))) return false;
            if (incTerms.length > 0 && !incTerms.some(t => sym.includes(t))) return false;
          }
          const pendingDate = p.filledTime ? String(p.filledTime).slice(0, 10) : '';
          if (!pendingDate || !range) return true;
          if (range.startDate && pendingDate < range.startDate) return false;
          if (range.endDate && pendingDate > range.endDate) return false;
          return true;
        });
        const total = filteredPending.reduce((s, p) => s + (Number(p.realizedPL) || 0), 0);
        if (!cancelled) setPendingPL(total);
      } catch (e) {
        if (!cancelled) setPendingPL(0);
      }
    };
    fetchPending();
    return () => { cancelled = true; };
  }, [selectedAccount, timePeriod, customStartDate, customEndDate, positionFilter, symbolFilter]);

  const handleTimePeriodChange = (event) => {
    const val = event.target.value;
    setTimePeriod(val);
    try { localStorage.setItem(modeKey('global_time_period'), val); } catch {}
  };

  const handleGroupByChange = (event) => {
    setGroupBy(event.target.value);
  };

  const handleAccountChange = (event) => {
    const {
      target: { value },
    } = event;
    
    const newValue = typeof value === 'string' ? value.split(',') : value;
    
    // Group header selection (sentinel values) - toggle all accounts in the group
    const groupSentinel = newValue.find(v => v === 'group:taxable' || v === 'group:non-taxable');
    if (groupSentinel) {
      handleAccountCategorySelect(groupSentinel === 'group:taxable' ? 'taxable' : 'non-taxable');
      return;
    }
    
    // If "all" is selected, clear other selections
    if (newValue.includes('all') && !draftAccount.includes('all')) {
      setDraftAccount(['all']);
      return;
    }
    
    // If "all" was selected and we add another, remove "all"
    if (draftAccount.includes('all') && newValue.length > 1) {
      setDraftAccount(newValue.filter(v => v !== 'all'));
      return;
    }
    
    // If nothing selected, default to "all"
    if (newValue.length === 0) {
      setDraftAccount(['all']);
      return;
    }
    
    setDraftAccount(newValue);
  };

  const applyAccountSelection = () => {
    setSelectedAccount(draftAccount);
  };

  const { privacyMask } = usePrivacyMask();

  // Format currency
  const formatCurrency = (value) => {
    if (privacyMask) return '••••••';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 4 }}>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
              <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                Closed Trade P/L
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }} color={(parseFloat(summaryStats.realizedPL) + pendingPL) > 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(parseFloat(summaryStats.realizedPL || 0) + pendingPL)}
              </Typography>
              {Math.abs(pendingPL) > 0.005 && (
                <Typography variant="caption" color="warning.main" display="block" sx={{ fontSize: '0.6rem' }}>
                  incl. {formatCurrency(pendingPL)} pending
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
              <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                Total Dividends
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {formatCurrency(summaryStats.dividend || 0)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
              <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                Unrealized P/L
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }} color={parseFloat(summaryStats.unrealizedPL) >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(summaryStats.unrealizedPL)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
              <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                Win Rate
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }}>
                {summaryStats.winRate}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
              <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                Profit Factor
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }}>
                {summaryStats.profitFactor}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
              <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                Avg Weekly P/L
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }} color={parseFloat(summaryStats.avgWeekly) >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(summaryStats.avgWeekly)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: isMobile ? '1 1 45%' : '1 1 0', minWidth: isMobile ? '45%' : 0 }}>
          <Card>
            <CardContent sx={{ p: isMobile ? 1 : 1.5, '&:last-child': { pb: isMobile ? 1 : 1.5 } }}>
              <Typography color="textSecondary" variant="caption" gutterBottom display="block" sx={{ fontSize: isMobile ? '0.65rem' : undefined }}>
                Avg Monthly P/L
              </Typography>
              <Typography variant={isMobile ? 'body2' : 'h6'} component="div" sx={{ fontWeight: 'bold' }} color={parseFloat(summaryStats.avgMonthly) >= 0 ? 'success.main' : 'error.main'}>
                {formatCurrency(summaryStats.avgMonthly)}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {!isMobile && (
      <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => setShowFilters(prev => !prev)}
          endIcon={showFilters ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
        >
          {showFilters ? 'Hide Filters' : 'More Filters'}
        </Button>
        <TextField
          variant="outlined"
          size="small"
          sx={{ minWidth: 150 }}
          label="Symbol"
          value={symbolFilter}
          onChange={(e) => setSymbolFilter(e.target.value)}
          placeholder="e.g. AAPL, -TSLA"
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Strategy</InputLabel>
          <Select
            value={strategyFilter}
            onChange={(e) => setStrategyFilter(e.target.value)}
            label="Strategy"
            renderValue={(v) => (STRATEGY_OPTIONS.find(o => o.value === v) || {}).label || v || 'All Strategies'}
          >
            {STRATEGY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Saved Filters</InputLabel>
          <Select
            value={selectedPreset}
            label="Saved Filters"
            onChange={(e) => handleSelectPreset(e.target.value)}
          >
            {Object.keys(savedFilters).length === 0 && (
              <MenuItem value="" disabled><em>None saved</em></MenuItem>
            )}
            {Object.keys(savedFilters).sort().map((name) => (
              <MenuItem key={name} value={name}>{name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <IconButton
          size="small"
          title="Save current filters as preset"
          onClick={() => setPresetDialog({ open: true, name: selectedPreset || '' })}
        >
          <SaveIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          title="Delete selected preset"
          disabled={!selectedPreset || !savedFilters[selectedPreset]}
          onClick={handleDeletePreset}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterAltOffIcon />}
          onClick={resetFilters}
        >
          Reset
        </Button>
        {(openDateStart || openDateEnd) && (
          <Typography variant="caption" color="text.secondary">
            Open date: {openDateStart || '…'} to {openDateEnd || '…'}
          </Typography>
        )}
      </Box>
      <Collapse in={showFilters}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Account</InputLabel>
              <Select
                multiple
                value={draftAccount}
                onChange={handleAccountChange}
                input={<OutlinedInput label="Account" />}
                renderValue={() => paperMode ? 'Paper Trading' : (() => {
                  const selected = draftAccount;
                  if (selected.includes('all')) return 'All Accounts';
                  if (selected.length === 1) {
                    const acc = accounts.find(a => a.id === selected[0]);
                    return acc ? acc.name : selected[0];
                  }
                  return `${selected.length} Accounts`;
                })()}
              >
                {paperMode ? (
                  <MenuItem value="all" dense>
                    <Checkbox size="small" checked />
                    <ListItemText primary="Paper Trading" />
                  </MenuItem>
                ) : ([
                <MenuItem key="all" value="all" dense>
                  <Checkbox size="small" checked={draftAccount.includes('all')} />
                  <ListItemText primary="All Accounts" />
                </MenuItem>,

                ...(taxableAccounts.length > 0 ? [
                  <MenuItem
                    key="taxable-header"
                    value="group:taxable"
                    dense
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.05)',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={taxableAccounts.length > 0 && taxableAccounts.every(a => draftAccount.includes(a.id))}
                      indeterminate={taxableAccounts.some(a => draftAccount.includes(a.id)) && !taxableAccounts.every(a => draftAccount.includes(a.id))}
                    />
                    <ListItemText primary="Taxable Accounts" primaryTypographyProps={{ fontWeight: 600 }} />
                  </MenuItem>,
                  ...taxableAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id} dense>
                      <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                      <ListItemText primary={account.name} />
                    </MenuItem>
                  ))
                ] : []),

                ...(nonTaxableAccounts.length > 0 ? [
                  <MenuItem
                    key="non-taxable-header"
                    value="group:non-taxable"
                    dense
                    sx={{
                      bgcolor: 'rgba(0,0,0,0.05)',
                      fontWeight: 600,
                      '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' }
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={nonTaxableAccounts.length > 0 && nonTaxableAccounts.every(a => draftAccount.includes(a.id))}
                      indeterminate={nonTaxableAccounts.some(a => draftAccount.includes(a.id)) && !nonTaxableAccounts.every(a => draftAccount.includes(a.id))}
                    />
                    <ListItemText primary="Non-Taxable Accounts" primaryTypographyProps={{ fontWeight: 600 }} />
                  </MenuItem>,
                  ...nonTaxableAccounts.map((account) => (
                    <MenuItem key={account.id} value={account.id} dense>
                      <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                      <ListItemText primary={account.name} />
                    </MenuItem>
                  ))
                ] : []),

                /* Show uncategorized if any (fallback) */
                ...displayAccounts.filter(a => !a.accountType).map((account) => (
                  <MenuItem key={account.id} value={account.id} dense>
                    <Checkbox size="small" checked={draftAccount.indexOf(account.id) > -1} />
                    <ListItemText primary={account.name} />
                  </MenuItem>
                ))
                ])}
              </Select>
            </FormControl>
            <Button 
              onClick={applyAccountSelection}
              variant="contained"
              size="small"
              disabled={JSON.stringify(draftAccount.sort()) === JSON.stringify(selectedAccount.sort())}
              sx={{ minWidth: '40px', px: 2 }}
            >
              OK
            </Button>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={timePeriod}
              onChange={handleTimePeriodChange}
              label="Time Period"
            >
              <MenuItem value="current_month">Current Month</MenuItem>
              <MenuItem value="previous_month">Previous Month</MenuItem>
              <MenuItem value="30d">30 Days</MenuItem>
              <MenuItem value="60d">60 Days</MenuItem>
              <MenuItem value="90d">90 Days</MenuItem>
              <MenuItem value="180d">180 Days</MenuItem>
              <MenuItem value="ytd">Year to Date</MenuItem>
              <MenuItem value="last_year">Last Year</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Group By</InputLabel>
            <Select
              value={groupBy}
              onChange={handleGroupByChange}
              label="Group By"
            >
              <MenuItem value="day">Day</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="month">Month</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {timePeriod === 'custom' && (
          <>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                sx={{ minWidth: 140 }}
                label="Start Date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                sx={{ minWidth: 140 }}
                label="End Date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Position Type</InputLabel>
            <Select
              multiple
              value={positionFilter}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes('all') && !positionFilter.includes('all')) {
                  setPositionFilter(['all']);
                  return;
                }
                if (positionFilter.includes('all') && value.length > 1) {
                  setPositionFilter(value.filter(v => v !== 'all'));
                  return;
                }
                if (value.length === 0) {
                  setPositionFilter(['all']);
                  return;
                }
                setPositionFilter(value);
              }}
              label="Position Type"
              renderValue={(selected) => {
                if (selected.includes('all')) return 'All Positions';
                return selected.map(v => {
                  if (v === 'options') return 'Options';
                  if (v === 'stocks') return 'Stocks';
                  if (v === 'dividends') return 'Dividends';
                  return v;
                }).join(', ');
              }}
            >
              <MenuItem value="all">
                <Checkbox checked={positionFilter.includes('all')} />
                <ListItemText primary="All Positions" />
              </MenuItem>
              <MenuItem value="options">
                <Checkbox checked={positionFilter.indexOf('options') > -1} />
                <ListItemText primary="Options Only" />
              </MenuItem>
              <MenuItem value="stocks">
                <Checkbox checked={positionFilter.indexOf('stocks') > -1} />
                <ListItemText primary="Stocks Only" />
              </MenuItem>
              <MenuItem value="dividends">
                <Checkbox checked={positionFilter.indexOf('dividends') > -1} />
                <ListItemText primary="Dividends" />
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 140 }}>
            <InputLabel shrink>Open Start</InputLabel>
            <OutlinedInput
              type="date"
              label="Open Start"
              notched
              value={openDateStart}
              onChange={(e) => setOpenDateStart(e.target.value)}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 140 }}>
            <InputLabel shrink>Open End</InputLabel>
            <OutlinedInput
              type="date"
              label="Open End"
              notched
              value={openDateEnd}
              onChange={(e) => setOpenDateEnd(e.target.value)}
            />
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 90 }}>
            <InputLabel>Tags</InputLabel>
            <Select
              multiple
              value={tagFilter}
              onChange={(e) => setTagFilter(e.target.value)}
              label="Tags"
              renderValue={(selected) => selected.join(', ')}
            >
              {availableTags.length === 0 && (
                <MenuItem disabled value=""><em>No tags</em></MenuItem>
              )}
              {availableTags.map(tag => (
                <MenuItem key={tag} value={tag} disabled={tagFilterExclude.includes(tag)}>
                  <Checkbox checked={tagFilter.includes(tag)} />
                  <ListItemText primary={tag} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Exclude Tags</InputLabel>
            <Select
              multiple
              value={tagFilterExclude}
              onChange={(e) => setTagFilterExclude(e.target.value)}
              label="Exclude Tags"
              renderValue={(selected) => selected.join(', ')}
            >
              {availableTags.length === 0 && (
                <MenuItem disabled value=""><em>No tags</em></MenuItem>
              )}
              {availableTags.map(tag => (
                <MenuItem key={tag} value={tag} disabled={tagFilter.includes(tag)}>
                  <Checkbox checked={tagFilterExclude.includes(tag)} />
                  <ListItemText primary={tag} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      </Collapse>

      {/* Save filter preset dialog */}
      <Dialog open={presetDialog.open} onClose={() => setPresetDialog({ open: false, name: '' })} maxWidth="xs" fullWidth>
        <DialogTitle>Save Filter Preset</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Preset Name"
            fullWidth
            variant="outlined"
            value={presetDialog.name}
            onChange={(e) => setPresetDialog(p => ({ ...p, name: e.target.value }))}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset(); }}
            helperText={savedFilters[presetDialog.name.trim()] ? 'A preset with this name exists and will be overwritten' : ''}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPresetDialog({ open: false, name: '' })} variant="contained" color="inherit">Cancel</Button>
          <Button onClick={handleSavePreset} variant="contained" color="primary" disabled={!presetDialog.name.trim()}>Save</Button>
        </DialogActions>
      </Dialog>
      </>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* P/L Chart and Top Winners/Losers */}
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Card>
                <CardContent>
              <Typography variant="h6" gutterBottom>
                Profit/Loss Over Time (Realized P/L)
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Shows realized gains/losses from closed trades only
              </Typography>
              <Box sx={{ height: isMobile ? 300 : 400, width: '100%', minWidth: 0, minHeight: 0 }}>
                <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
                  <ComposedChart
                    data={plData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <XAxis
                      dataKey="period"
                      tickFormatter={(value) => {
                        // 'YYYY-MM' periods are months — show month+year;
                        // 'YYYY-MM-DD' periods (day/week) show month+day.
                        if (/^\d{4}-\d{2}$/.test(value)) {
                          const date = new Date(`${value}-01`);
                          return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                        }
                        const date = new Date(value);
                        if (isNaN(date.getTime())) return value;
                        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      }}
                    />
                    {(() => {
                        // Calculate cumulative domain once
                        const cumulValues = plData.map(d => d.cumulativeRealizedPL || 0);
                        const cumulMin = Math.min(...cumulValues);
                        const cumulMax = Math.max(...cumulValues);
                        const cumulRange = cumulMax - cumulMin;
                        
                        const rawCumulInterval = cumulRange / 5;
                        const cumulMagnitude = Math.pow(10, Math.floor(Math.log10(rawCumulInterval)));
                        const normalizedCumulInterval = rawCumulInterval / cumulMagnitude;
                        let niceCumulInterval;
                        if (normalizedCumulInterval <= 1) niceCumulInterval = 1;
                        else if (normalizedCumulInterval <= 2) niceCumulInterval = 2;
                        else if (normalizedCumulInterval <= 5) niceCumulInterval = 5;
                        else niceCumulInterval = 10;
                        niceCumulInterval *= cumulMagnitude;
                        
                        const cumulPadding = niceCumulInterval * 0.5;
                        const niceCumulMin = Math.floor(cumulMin / niceCumulInterval) * niceCumulInterval - cumulPadding;
                        const niceCumulMax = Math.ceil(cumulMax / niceCumulInterval) * niceCumulInterval + cumulPadding;
                        
                        // Calculate where zero falls on cumulative scale (0 to 1)
                        const cumulZeroRatio = (0 - niceCumulMin) / (niceCumulMax - niceCumulMin);
                        
                        // Calculate period domain
                        const periodValues = plData.map(d => d.realizedPL || 0);
                        const periodMin = Math.min(...periodValues);
                        const periodMax = Math.max(...periodValues);
                        const periodRange = periodMax - periodMin;
                        
                        const rawPeriodInterval = periodRange / 5;
                        const periodMagnitude = Math.pow(10, Math.floor(Math.log10(rawPeriodInterval)));
                        const normalizedPeriodInterval = rawPeriodInterval / periodMagnitude;
                        let nicePeriodInterval;
                        if (normalizedPeriodInterval <= 1) nicePeriodInterval = 1;
                        else if (normalizedPeriodInterval <= 2) nicePeriodInterval = 2;
                        else if (normalizedPeriodInterval <= 5) nicePeriodInterval = 5;
                        else nicePeriodInterval = 10;
                        nicePeriodInterval *= periodMagnitude;
                        
                        // Calculate period domain to align zero at same position as cumulative
                        const periodTotalTicks = 10;
                        const periodTotalRange = nicePeriodInterval * periodTotalTicks;
                        const periodZeroOffset = periodTotalRange * cumulZeroRatio;
                        const nicePeriodMin = -periodZeroOffset;
                        const nicePeriodMax = periodTotalRange - periodZeroOffset;
                        
                        return (
                          <>
                            <YAxis 
                              yAxisId="left" 
                              domain={[niceCumulMin, niceCumulMax]}
                              tickFormatter={(value) => Math.round(value).toLocaleString()}
                            />
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              domain={[nicePeriodMin, nicePeriodMax]}
                              tickFormatter={(value) => Math.round(value).toLocaleString()}
                            />
                          </>
                        );
                      })()}
                    <Tooltip
                      contentStyle={{
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 4,
                        color: theme.palette.text.primary
                      }}
                      itemStyle={{ color: theme.palette.text.primary }}
                      labelStyle={{ color: theme.palette.text.secondary }}
                      formatter={(value) => formatCurrency(value)}
                      labelFormatter={(label) => {
                        const date = new Date(label);
                        if (isNaN(date.getTime())) return label; // Return original label if invalid date
                        return date.toISOString().split('T')[0]; // Format as yyyy-mm-dd
                      }}
                    />
                    <Legend />
                    <Area 
                      type="monotone"
                      dataKey="cumulativeRealizedPL"
                      stroke="#8884d8"
                      fill="#8884d8"
                      fillOpacity={0.2}
                      name="Cumulative P/L (Realized)"
                      yAxisId="left"
                      activeDot={{ r: 8 }}
                    />
                    <Bar
                      dataKey="realizedPL"
                      name="Period P/L (Realized)"
                      yAxisId="right"
                      barSize={20}
                    >
                      {plData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.realizedPL >= 0 ? '#4caf50' : '#f44336'} 
                        />
                      ))}
                    </Bar>

                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
                </CardContent>
              </Card>
            </Box>
            {!isMobile && (
            <Box sx={{ width: 200 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Top 5 Winners
                  </Typography>
                  <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                    {topWinners.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No winning trades
                      </Typography>
                    ) : (
                      topWinners.map((trade, index) => (
                        <Box
                          key={`winner-${index}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 0.5,
                            borderBottom: index < topWinners.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {trade.symbol}
                          </Typography>
                          <Typography variant="caption" color="success.main">
                            {formatCurrency(trade.pl)}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Top 5 Losers
                  </Typography>
                  <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                    {topLosers.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">
                        No losing trades
                      </Typography>
                    ) : (
                      topLosers.map((trade, index) => (
                        <Box
                          key={`loser-${index}`}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            py: 0.5,
                            borderBottom: index < topLosers.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                          }}
                        >
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {trade.symbol}
                          </Typography>
                          <Typography variant="caption" color="error.main">
                            {formatCurrency(trade.pl)}
                          </Typography>
                        </Box>
                      ))
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
            )}
          </Box>

          {/* Account Balance History Chart + Strategy P/L Table */}
          <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 4 }}>
            <Box sx={{ flex: 1, maxWidth: isMobile ? '100%' : 'calc(100% - 460px)' }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="h6">
                  {paperMode ? 'Paper Account Balance Over Time' : 'Account Balance Over Time'}
                </Typography>
                {balanceChange && (
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={balanceChange.change >= 0 ? 'success.main' : 'error.main'}
                  >
                    {balanceChange.change >= 0 ? '+' : '-'}{formatCurrency(Math.abs(balanceChange.change))}
                    {' '}({balanceChange.change >= 0 ? '+' : '-'}{Math.abs(balanceChange.pct).toFixed(1)}%)
                  </Typography>
                )}
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                {paperMode ? 'Cash balance computed from paper trade cash flows (debit/credit)' : 'Daily equity snapshots from account balance snapshots'}
              </Typography>
              {balanceLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : balanceHistory.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No balance history available. Balance snapshots are saved daily when the scheduler runs.
                </Typography>
              ) : (
                <Box sx={{ height: isMobile ? 250 : 300, width: '100%' }}>
                  <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                    <AreaChart
                      data={balanceHistory}
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <defs>
                        <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#1976d2" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#1976d2" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <XAxis
                        dataKey="date"
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          if (isNaN(date.getTime())) return value;
                          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        }}
                      />
                      <YAxis
                        tickFormatter={(value) => formatCurrency(value)}
                        domain={['auto', 'auto']}
                      />
                      <Tooltip
                        formatter={(value) => formatCurrency(value)}
                        labelFormatter={(label) => {
                          const date = new Date(label);
                          if (isNaN(date.getTime())) return label;
                          return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
                        }}
                        contentStyle={{
                          backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#fff',
                          border: `1px solid ${theme.palette.mode === 'dark' ? '#666' : '#ccc'}`,
                          borderRadius: '4px',
                        }}
                        labelStyle={{
                          color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                        }}
                        itemStyle={{
                          color: theme.palette.mode === 'dark' ? '#fff' : '#000',
                        }}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="equity"
                        stroke="#1976d2"
                        fill="url(#colorEquity)"
                        name="Total Equity"
                        strokeWidth={2}
                        activeDot={{ r: 6 }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </CardContent>
          </Card>
            </Box>
            {!isMobile && (
            <Box sx={{ width: 440 }}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    P/L by Strategy
                  </Typography>
                  {strategyPL.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No strategy data available
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 330 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell><strong>Strategy</strong></TableCell>
                            <TableCell align="right"><strong>P/L</strong></TableCell>
                            <TableCell align="right"><strong>% P/L</strong></TableCell>
                            <TableCell align="right"><strong>Trades</strong></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {strategyPL.map((s, idx) => (
                            <TableRow key={`strat-${idx}`} hover>
                              <TableCell sx={{ fontSize: '0.75rem' }}>{s.strategy}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                <Typography
                                  variant="caption"
                                  color={s.pl >= 0 ? 'success.main' : 'error.main'}
                                  sx={{ fontWeight: 600 }}
                                >
                                  {formatCurrency(s.pl)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                <Typography
                                  variant="caption"
                                  color={s.plPct >= 0 ? 'success.main' : 'error.main'}
                                >
                                  {s.plPct > 0 ? '+' : ''}{s.plPct}%
                                </Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{s.count}</TableCell>
                            </TableRow>
                          ))}
                          <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                              {formatCurrency(strategyPL.reduce((sum, s) => sum + s.pl, 0))}
                            </TableCell>
                            <TableCell align="right" />
                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                              {strategyPL.reduce((sum, s) => sum + s.count, 0)}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Box>
            )}
          </Box>

          {/* Weekly Earnings Calendar - hidden on mobile */}
          {!isMobile && (
          <Card sx={{ mb: 4 }}>
            <CardContent sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Weekly Earnings Calendar (Next 5 Days)
              </Typography>
              {earningsLoading ? (
                <CircularProgress size={20} />
              ) : sortedDates.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No upcoming earnings in the next 5 days
                </Typography>
              ) : (
                <Grid container spacing={1}>
                  {sortedDates.map((date, dateIndex) => (
                    <Grid item xs={12} sm={6} md={2.4} key={date}>
                      <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 1, height: '100%' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                          {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </Typography>
                        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                          {earningsByDate[date].map((earning, index) => (
                            <Box
                              key={`${earning.symbol}-${index}`}
                              sx={{
                                py: 0.25,
                                borderBottom: index < earningsByDate[date].length - 1 ? '1px solid #f0f0f0' : 'none'
                              }}
                            >
                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                {earning.symbol}
                              </Typography>
                              {earning.company_name && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'textSecondary', fontSize: '0.65rem' }}>
                                  {earning.company_name}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default Dashboard;